import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, Eye, Printer, Clock, CheckCircle, AlertCircle, Loader2, Plus, Edit2 } from 'lucide-react';
import { Button } from '../components/ui/button';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
function getToken() {
  return localStorage.getItem('ervapos_token') || localStorage.getItem('token') || '';
}
function authHeaders() {
  return { Authorization: 'Bearer ' + getToken() };
}

export default function RotulosPage() {
  const navigate = useNavigate();
  const [produtos, setProdutos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [selected, setSelected] = useState(null);
  const [historico, setHistorico] = useState([]);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [printLoading, setPrintLoading] = useState(false);
  const [quantidade, setQuantidade] = useState(1);
  const [toast, setToast] = useState(null);
  const iframeRef = useRef(null);

  const showToast = (msg, tipo) => {
    setToast({ msg, tipo: tipo || 'ok' });
    setTimeout(() => setToast(null), 3500);
  };

  const loadProdutos = () => {
    fetch(API_BASE + '/labels/produtos', { headers: authHeaders() })
      .then(r => r.json())
      .then(data => { setProdutos(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => { setErro('Erro ao carregar produtos.'); setLoading(false); });
  };

  const loadHistorico = () => {
    fetch(API_BASE + '/labels/history?limit=20', { headers: authHeaders() })
      .then(r => r.json())
      .then(data => setHistorico(Array.isArray(data) ? data : []))
      .catch(() => {});
  };

  useEffect(() => { loadProdutos(); loadHistorico(); }, []);

  const handlePreview = async (produto) => {
    setSelected(produto);
    setPreviewUrl('');
    setPreviewLoading(true);
    try {
      const res = await fetch(
        API_BASE + '/labels/preview/' + produto.id + '?lote=PREVIEW&validade=12/2099',
        { headers: authHeaders() }
      );
      if (!res.ok) throw new Error('Erro');
      const blob = await res.blob();
      setPreviewUrl(URL.createObjectURL(blob));
    } catch(e) {
      showToast('Erro ao gerar pre-visualizacao.', 'erro');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!selected) return;
    setPrintLoading(true);
    try {
      const res = await fetch(API_BASE + '/labels/render', {
        method: 'POST',
        headers: Object.assign({}, authHeaders(), { 'Content-Type': 'application/json' }),
        body: JSON.stringify({ produto_id: selected.id, lote: '', validade: '', quantidade: quantidade }),
      });
      if (!res.ok) throw new Error('Erro');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'rotulo_' + selected.id + '_x' + quantidade + '.pdf';
      a.click();
      URL.revokeObjectURL(url);
      showToast('Rotulo(s) gerado(s) com sucesso');
      loadHistorico();
    } catch(e) {
      showToast('Erro ao gerar PDF.', 'erro');
    } finally {
      setPrintLoading(false);
    }
  };

  const tipoLabel = (p) => p.tipo === 'liquido' ? (p.volume || '') : (p.num_unidades || '');

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {toast && (
        <div className={'fixed top-6 right-6 z-50 flex items-center gap-3 px-5 py-3 rounded-xl shadow-xl text-sm font-medium ' + (toast.tipo === 'erro' ? 'bg-rose-600' : 'bg-emerald-600')}>
          {toast.tipo === 'erro' ? React.createElement(AlertCircle, {className: 'w-4 h-4'}) : React.createElement(CheckCircle, {className: 'w-4 h-4'})}
          <span>{toast.msg}</span>
        </div>
      )}

      <div className="border-b border-slate-800 bg-slate-900/60 px-8 py-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/15 flex items-center justify-center">
              <Package className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">Rotulos Elemensys</h1>
              <p className="text-sm text-slate-400">Pre-visualizacao e impressao de rotulos de produto</p>
            </div>
          </div>
          <Button onClick={() => navigate('/rotulos/novo')} className="bg-emerald-600 hover:bg-emerald-700">
            <Plus className="w-4 h-4 mr-2" />
            Novo Produto
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-[340px_1fr] h-[calc(100vh-140px)]">
        <div className="border-r border-slate-800 flex flex-col">
          <div className="px-5 py-4 border-b border-slate-800">
            <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Produtos disponíveis</p>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {loading && <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 text-emerald-400 animate-spin" /></div>}
            {erro && <p className="text-rose-400 text-sm p-4">{erro}</p>}
            {!loading && produtos.length === 0 && (
              <div className="text-center py-12">
                <p className="text-slate-500 text-sm">Sem produtos configurados.</p>
                <Button onClick={() => navigate('/rotulos/novo')} variant="outline" size="sm" className="mt-3 border-slate-700 text-slate-400">
                  <Plus className="w-3 h-3 mr-1" /> Criar primeiro produto
                </Button>
              </div>
            )}
            {!loading && produtos.map(function(p) {
              var isActive = selected && selected.id === p.id;
              return (
                <div key={p.id} className={'rounded-xl border transition-all ' + (isActive ? 'bg-emerald-500/15 border-emerald-500/40' : 'bg-slate-900/40 border-slate-800')}>
                  <button onClick={() => handlePreview(p)} className="w-full text-left px-4 py-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-white truncate">
                          {p.nome_comercial} <span className="text-emerald-400 font-normal italic">{p.subtitulo}</span>
                        </p>
                        <p className="text-xs text-slate-400 mt-0.5">{tipoLabel(p)}</p>
                        <p className="text-xs text-slate-500">{p.peso_liquido}</p>
                      </div>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 flex-shrink-0 mt-0.5">
                        {p.tipo === 'liquido' ? 'Liquido' : 'Solido'}
                      </span>
                    </div>
                  </button>
                  <div className="px-3 pb-2">
                    <button onClick={() => navigate('/rotulos/editar/' + p.id)}
                      className="text-xs text-slate-500 hover:text-emerald-400 flex items-center gap-1 transition-colors">
                      <Edit2 className="w-3 h-3" /> Editar produto
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
          {historico.length > 0 && (
            <div className="border-t border-slate-800">
              <div className="px-5 py-3 border-b border-slate-800">
                <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-2">
                  <Clock className="w-3 h-3" /> Ultimas impressoes
                </p>
              </div>
              <div className="max-h-40 overflow-y-auto p-3 space-y-1">
                {historico.slice(0, 8).map(function(h, i) {
                  return (
                    <div key={i} className="text-xs text-slate-400 px-2 py-1.5 rounded-lg bg-slate-900/40 flex justify-between gap-2">
                      <span className="truncate text-slate-300">{h.produto_nome}</span>
                      <span className="text-slate-500 flex-shrink-0">{h.quantidade}x</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-col bg-slate-950">
          {!selected ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
              <div className="w-20 h-20 rounded-2xl bg-slate-900 flex items-center justify-center mb-4">
                <Package className="w-10 h-10 text-slate-700" />
              </div>
              <p className="text-lg font-semibold text-slate-400">Selecciona um produto</p>
              <p className="text-sm text-slate-600 mt-1">O rotulo aparece aqui para pre-visualizacao</p>
            </div>
          ) : (
            <div className="flex flex-col h-full">
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800 bg-slate-900/40">
                <div>
                  <p className="text-sm font-semibold text-white">
                    {selected.nome_comercial}
                    <span className="text-emerald-400 italic font-normal ml-1">{selected.subtitulo}</span>
                  </p>
                  <p className="text-xs text-slate-400 mt-0.5">{tipoLabel(selected)} - {selected.peso_liquido}</p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 bg-slate-900 border border-slate-700 rounded-lg px-3 py-1.5">
                    <button onClick={() => setQuantidade(function(q) { return Math.max(1, q-1); })} className="text-slate-400 hover:text-white font-bold w-5 text-center">-</button>
                    <span className="text-white text-sm font-semibold w-6 text-center">{quantidade}</span>
                    <button onClick={() => setQuantidade(function(q) { return Math.min(500, q+1); })} className="text-slate-400 hover:text-white font-bold w-5 text-center">+</button>
                  </div>
                  <Button onClick={() => handlePreview(selected)} variant="outline" size="sm"
                    className="border-slate-700 text-slate-300 hover:text-white" disabled={previewLoading}>
                    {previewLoading ? React.createElement(Loader2, {className: 'w-4 h-4 animate-spin mr-2'}) : React.createElement(Eye, {className: 'w-4 h-4 mr-2'})}
                    Actualizar
                  </Button>
                  <Button onClick={handleDownload} size="sm" className="bg-emerald-600 hover:bg-emerald-700" disabled={printLoading}>
                    {printLoading ? React.createElement(Loader2, {className: 'w-4 h-4 animate-spin mr-2'}) : React.createElement(Printer, {className: 'w-4 h-4 mr-2'})}
                    Imprimir {quantidade > 1 ? '(' + quantidade + 'x)' : ''}
                  </Button>
                </div>
              </div>
              <div className="flex-1 relative bg-slate-900/20">
                {previewLoading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-slate-950/80 z-10">
                    <div className="flex flex-col items-center gap-3">
                      <Loader2 className="w-8 h-8 text-emerald-400 animate-spin" />
                      <p className="text-sm text-slate-400">A gerar rotulo...</p>
                    </div>
                  </div>
                )}
                {previewUrl && (
                  <iframe ref={iframeRef} src={previewUrl} title="Pre-visualizacao"
                    className="w-full h-full border-0" style={{ background: '#fff' }} />
                )}
              </div>
              <div className="px-6 py-3 border-t border-slate-800 bg-slate-900/40">
                <p className="text-xs text-slate-500 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-amber-400 flex-shrink-0"></span>
                  O espaco tracejado (35x25mm) e para a etiqueta QR com lote e validade - colada manualmente.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
